import * as React from "react";
import EdButton from "../../generic/edButton";
import InputField from "../../generic/inputField";
import "../../../assets/scss/components/login/forms/leadForm.scss";
import "../../../assets/scss/components/login/generic/loginInput.scss";
import { emailRegex, betweenNChars, phoneNo, alphabetExp, integerExp } from "../../utils/regex";
import Autocomplete from "../../generic/autocomplete";
import debounce from "../../utils/debounce";
import { AGetCities } from "../../service/login/loginApiService";
import LoginErrMsg from "../generic/loginErrMsg";

export interface ILeadFormProps {
	errorMsg?: string; // used to show error message if any
	disableForm: boolean; // used to disable form from parent
	onSubmit: (values: ILeadFormFields) => void; // function to be called on submit
	onRemoveError: () => void; // removes the errorMsg
}

export interface ILeadFormFields {
	nm: string;
	eml: string;
	phNo: string;
	city: string;
	state: string;
}

const LeadForm: React.FC<ILeadFormProps> = props => {
	// used to store the entered values in form
	const [values, setValues] = React.useState<ILeadFormFields>({ nm: "", eml: "", phNo: "", city: "", state: "" });

	// used to store the field wise errors
	const [errors, setErrors] = React.useState<{ [key in keyof ILeadFormFields]?: string }>({
		nm: undefined,
		eml: undefined,
		phNo: undefined,
		city: undefined
	});

	// cities list
	const [cities, setCities] = React.useState<Array<any> | null>(null);

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		props.onSubmit(values);
	}

	function onChange(key: keyof ILeadFormFields, value: string) {
		// onchange of any field remove both type of errors
		if (key === "city") {
			// separate city and state (state is blank string if not present in the value)
			const [city, state = ""] = value.split(";");
			setValues({ ...values, city, state });

			// if city length is greater than 2, then call the api with debounce of 300ms
			if (city.length > 2) {
				debounce(() => getCities(value), 300)();
			}
		} else {
			if ((key === "nm" && !alphabetExp.test(value)) || (key === "phNo" && !integerExp.test(value))) {
				value = values[key];
			}
			setValues({ ...values, [key]: value });
		}
		setErrors({ ...errors, [key]: "" });
		props.onRemoveError();

		return value;
	}

	function getCities(srchStr: string) {
		AGetCities(getCitiesRes, srchStr);
	}

	function getCitiesRes(res: any) {
		if (res.data && res.data.ctyLst) {
			setCities(res.data.ctyLst);
		}
	}

	function handleACBlur(value: string) {
		if (value.split(";").length !== 2) {
			setErrors({ ...errors, city: "Please select a city from dropdown" });
		}
	}

	return (
		<form onSubmit={handleSubmit}>
			<label className="inp_lbl mg-b5 ib">Name</label>
			<InputField
				pattern={betweenNChars(3)}
				onChange={value => onChange("nm", value)}
				onBlur={error => setErrors({ ...errors, nm: error.pattern ? "" : "Name should be atleast 3 characters" })}
				className="login_inp mg-b40"
				placeholder="Eg:Rajesh Patel"
				errorText={errors.nm}
			/>

			<label className="inp_lbl mg-b5 ib">Email ID</label>
			<InputField
				pattern={emailRegex}
				onChange={value => onChange("eml", value)}
				onBlur={error => setErrors({ ...errors, eml: error.pattern ? "" : "Invalid Email" })}
				className="login_inp mg-b40"
				placeholder="Eg:rajeshpatel@gmail.com"
				errorText={errors.eml}
			/>

			<label className="inp_lbl mg-b5 ib">Phone Number</label>
			<div className="phone_no">
				<span className="cntry_code mg-b30 ib mg-r20">+91</span>
				<InputField
					type="tel"
					pattern={phoneNo}
					onChange={value => onChange("phNo", value)}
					onBlur={error => setErrors({ ...errors, phNo: error.pattern ? "" : "Invalid Phone Number" })}
					className="login_inp mg-b30"
					placeholder="Eg: 9809807765"
					errorText={errors.phNo}
				/>
			</div>

			<label className="inp_lbl mg-b5 ib">City</label>
			{/* We have to store state also along with city because state is required in api request.
					Index of cities is not stored in value because it may change when user clicks on a city (api is called again)
					That is why both city and state are stored in value
					This also helps to check if the value in input is typed by the user or selected from the dropdown */}
			<Autocomplete
				data={cities ? cities.map(city => ({ value: city.city + ";" + city.state, display: city.city })) : null}
				onOptionSelect={value => onChange("city", value.value as string)}
				noDataMsg="City with similar name not found"
				onChange={value => onChange("city", value)}
				inputClassName="login_inp mg-b40"
				placeholder="Eg:Mumbai"
				onBlur={handleACBlur}
				name="city"
				className="city"
			/>

			{/* Show error if any */}
			{props.errorMsg && <LoginErrMsg className="mg-b40">{props.errorMsg}</LoginErrMsg>}

			<EdButton
				value="Submit"
				btnTyp="primary"
				className="submit_btn"
				disabled={Object.values(errors).every(error => error) || Object.values(values).some(value => !value) || props.disableForm}
			/>
		</form>
	);
};

export default LeadForm;
